'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @a11y-contract Notice (client feedback — collapsed Banner/Alert/Callout)
 *
 * Role:           role="alert"  when intent="danger"  (assertive live region).
 *                 role="status" when intent≠"danger"  (polite live region).
 *                 Both roles make the content announced to AT without focus.
 * RSC:            No — 'use client' (dismissible state, ESC handler, focus restore).
 * Focus:          On dismiss, focus is returned to the element that was focused
 *                 before the Notice appeared (stored in a ref at mount).
 *                 <Notice.Action> renders a focusable button inside the notice.
 *                 Dismiss button: last focusable element in the notice.
 * Keyboard:       ESC dismisses only when dismissible=true.
 *                 Tab traverses action button → dismiss button naturally.
 * Forced-colors:  Intent conveyed by icon + text, not colour alone (Pillar 14).
 *                 Background is suppressed; border drawn in ButtonText.
 * RTL:            Leading icon + content + dismiss button use flex + logical gap.
 * Reduced-motion: Dismiss animation respects prefers-reduced-motion.
 * Touch target:   Dismiss IconButton ≥ 44×44.
 * Labels:         role=alert/status provides live-region announcement.
 *                 Dismiss button: aria-label="Dismiss notification".
 *                 Intent icon: aria-hidden (intent announced via title text).
 * Live region:    role=alert (danger) or role=status (others) IS the live region.
 *                 announceOn:
 *                   "mount"      → live region present in DOM immediately (danger default).
 *                   "transition" → live region inserted after mount transition (default for others).
 *                   "always"     → aria-live="assertive" even for non-danger intents.
 * PRM (Pillar 12):padding/gap use --space-* tokens.
 */
import { useEffect, useRef, useState, createContext, } from 'react';
import { X, Info, CheckCircle, AlertTriangle, AlertCircle } from 'lucide-react';
import styles from './Notice.module.css';
const INTENT_ICON = {
    info: _jsx(Info, { size: 16, "aria-hidden": "true" }),
    success: _jsx(CheckCircle, { size: 16, "aria-hidden": "true" }),
    warning: _jsx(AlertTriangle, { size: 16, "aria-hidden": "true" }),
    danger: _jsx(AlertCircle, { size: 16, "aria-hidden": "true" }),
};
function NoticeAction({ children, onClick }) {
    return (_jsx("button", { type: "button", className: styles.action, onClick: onClick, children: children }));
}
// ---------------------------------------------------------------------------
// NoticeContext — passes intent down to Action
// ---------------------------------------------------------------------------
const NoticeContext = createContext({ intent: 'info' });
export function Notice({ intent = 'info', placement = 'inline', title, children, dismissible, onDismiss, announceOn, className, }) {
    // Danger: not dismissible by default. Others: dismissible by default.
    const isDismissible = dismissible !== undefined ? dismissible : intent !== 'danger';
    // Default announceOn: "mount" for danger, "transition" for others.
    const effectiveAnnounceOn = announceOn ?? (intent === 'danger' ? 'mount' : 'transition');
    const [visible, setVisible] = useState(true);
    const [liveActive, setLiveActive] = useState(effectiveAnnounceOn === 'mount');
    // Ref to restore focus on dismiss
    const previousFocusRef = useRef(null);
    useEffect(() => {
        previousFocusRef.current = document.activeElement;
    }, []);
    useEffect(() => {
        if (effectiveAnnounceOn === 'transition') {
            // Small delay — ensures the DOM is rendered before AT announces
            const id = setTimeout(() => setLiveActive(true), 100);
            return () => clearTimeout(id);
        }
        if (effectiveAnnounceOn === 'always') {
            setLiveActive(true);
        }
        return undefined;
    }, [effectiveAnnounceOn]);
    function dismiss() {
        if (!isDismissible)
            return;
        setVisible(false);
        onDismiss?.();
        // Restore focus to previous element
        if (previousFocusRef.current &&
            'focus' in previousFocusRef.current &&
            typeof previousFocusRef.current.focus === 'function') {
            previousFocusRef.current.focus();
        }
    }
    function handleKeyDown(e) {
        if (e.key === 'Escape' && isDismissible) {
            e.stopPropagation();
            dismiss();
        }
    }
    if (!visible)
        return null;
    const role = intent === 'danger' || effectiveAnnounceOn === 'always' ? 'alert' : 'status';
    const ariaLive = intent === 'danger' || effectiveAnnounceOn === 'always' ? 'assertive' : 'polite';
    const cls = [
        styles.root,
        styles[`intent--${intent}`],
        styles[`placement--${placement}`],
        className,
    ]
        .filter(Boolean)
        .join(' ');
    return (_jsx(NoticeContext.Provider, { value: { intent }, children: _jsxs("div", { role: liveActive ? role : undefined, "aria-live": liveActive ? ariaLive : undefined, "aria-atomic": "true", className: cls, onKeyDown: handleKeyDown, children: [_jsx("span", { className: styles.icon, "aria-hidden": "true", children: INTENT_ICON[intent] }), _jsxs("div", { className: styles.body, children: [_jsx("span", { className: styles.title, children: title }), children && _jsx("div", { className: styles.content, children: children })] }), isDismissible && (_jsx("button", { type: "button", className: styles.dismiss, onClick: dismiss, "aria-label": "Dismiss notification", children: _jsx(X, { size: 16, "aria-hidden": "true" }) }))] }) }));
}
Notice.Action = NoticeAction;
//# sourceMappingURL=Notice.js.map